import { useDispatchPromise } from '../../../utils/AppUtils';
import { postInfluencerOAuthConnect } from '../../../redux/buzzbridge/actions';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import SmartDisplayOutlinedIcon from '@mui/icons-material/SmartDisplayOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { IInfluencer, IPlatformStats } from '../../../redux/buzzbridge/types';
import { Colors } from '../../../constants/theme';
import { SocialLogin } from '../../SocialLogin/SocialLogin';

import { connectedPlatformsUrl } from '../../../configurations/api/url';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../redux/root-reducer';
import { buzzBridgeConfigSelector } from '../../../selectors/selectors';
import { resolveInstagramConnectProvider } from '../../../utils/instagramOAuthProvider';
import { formatSocialHandle } from '../../../utils/socialHandle';
import { isInfluencerPlatformAutoLinkEnabled } from '../../../utils/influencerSocialAutoLink';
import { LOCKED_PLATFORMS } from '../CampaignApplicationForm';
import { ManualPlatformLinkForm } from '../ManualPlatformLinkForm';
import {
    EMPTY_MANUAL_PLATFORM_LINK,
    isManualPlatformLinkComplete,
    manualPlatformLinkFromStats,
    manualPlatformLinkToStats,
    type IManualPlatformLinkData,
} from '../../../utils/manualPlatformLinkUtils';
import { resolveInfluencerDisplayAvatarUrl } from '../../../utils/influencerSocialAvatar';

type ThemeStyle = 'light' | 'dark';
type PlatformKey = 'instagram' | 'tiktok' | 'youtube';

interface IConnectedPlatform {
    platform: PlatformKey;
    platform_username: string;
    last_refreshed_at?: string;
}

function formatCompactNumber(n: number | undefined): string {
    if (n === undefined || Number.isNaN(n)) return '—';
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
    return String(Math.round(n));
}

function formatPercent(rate: number | undefined): string {
    if (rate === undefined || Number.isNaN(rate)) return '—';
    return `${rate}%`;
}

const PLATFORM_META: Record<
    PlatformKey,
    {
        label: string;
        provider: 'meta' | 'instagram' | 'tiktok' | 'youtube';
        gradient: string;
        icon: (size: number) => React.ReactNode;
    }
> = {
    instagram: {
        label: 'Instagram',
        provider: 'meta',
        gradient: 'linear-gradient(135deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)',
        icon: size => <PhotoCameraOutlinedIcon sx={{ fontSize: size }} />,
    },
    tiktok: {
        label: 'TikTok',
        provider: 'tiktok',
        gradient: 'linear-gradient(135deg, #010101 0%, #1a1a2e 100%)',
        icon: size => <MusicNoteIcon sx={{ fontSize: size }} />,
    },
    youtube: {
        label: 'YouTube',
        provider: 'youtube',
        gradient: 'linear-gradient(135deg, #ff0000 0%, #cc0000 100%)',
        icon: size => <SmartDisplayOutlinedIcon sx={{ fontSize: size }} />,
    },
};

interface ManualFormState extends IManualPlatformLinkData {}

const emptyManual = (): ManualFormState => ({ ...EMPTY_MANUAL_PLATFORM_LINK });

interface EditProfileConnectedPlatformCardsProps {
    formData: Partial<IInfluencer>;
    themeTokens: Colors;
    themeStyle: ThemeStyle;
    onPlatformConnected?: () => void;
    onManualSave?: (data: Partial<IInfluencer> & { rawProfile?: File[] }) => void;
}

export const EditProfileConnectedPlatformCards: React.FC<EditProfileConnectedPlatformCardsProps> = ({
    formData,
    themeTokens,
    themeStyle,
    onPlatformConnected,
    onManualSave,
}) => {
    const dispatchPromise = useDispatchPromise();
    const t = themeTokens;
    const token: string = useSelector((s: RootState) => s.authentication.token);
    const buzzBridgeConfig = useSelector(buzzBridgeConfigSelector);
    const [actionPlatform, setActionPlatform] = useState<PlatformKey | null>(null);
    const [errors, setErrors] = useState<Partial<Record<PlatformKey, string>>>({});
    const [connectedPlatforms, setConnectedPlatforms] = useState<IConnectedPlatform[]>([]);
    const [manualOpen, setManualOpen] = useState<Partial<Record<PlatformKey, boolean>>>({});
    const [manualForms, setManualForms] = useState<Partial<Record<PlatformKey, ManualFormState>>>({});

    const getManualForm = (key: PlatformKey): ManualFormState => manualForms[key] ?? emptyManual();

    const handleManualToggle = (key: PlatformKey) => {
        setManualOpen(prev => {
            const isOpen = Boolean(prev[key]);
            return isOpen ? {} : { [key]: true };
        });
        if (!manualForms[key]) {
            const existing = formData[key];
            if (existing) {
                setManualForms(prev => ({
                    ...prev,
                    [key]: manualPlatformLinkFromStats(existing),
                }));
            }
        }
    };

    const handleManualFieldChange = (key: PlatformKey, data: ManualFormState) => {
        setManualForms(prev => ({
            ...prev,
            [key]: data,
        }));
    };

    const handleManualSubmit = (key: PlatformKey) => {
        const form = getManualForm(key);
        const existingHeadshotUrl = resolveInfluencerDisplayAvatarUrl(formData as IInfluencer);
        if (!isManualPlatformLinkComplete(form, { existingHeadshotUrl })) return;

        const payload: Partial<IInfluencer> & { rawProfile?: File[] } = {
            [key]: manualPlatformLinkToStats(form),
        };
        if (form.headshotFile) {
            payload.rawProfile = [form.headshotFile];
        }
        onManualSave?.(payload);
        setManualOpen(prev => ({ ...prev, [key]: false }));
    };

    const manualLinkLabels = (key: PlatformKey) => {
        if (key === 'youtube') {
            return { usernameLabel: 'Channel name', followersLabel: 'Total subscribers' };
        }
        return { usernameLabel: 'Username', followersLabel: 'Total followers' };
    };

    const cardBg = themeStyle === 'dark' ? '#131313' : t.secondaryBg;
    const borderColor = themeStyle === 'dark' ? 'rgba(255,255,255,0.06)' : t.form_divider;

    const fetchConnected = useCallback(() => {
        axios
            .get<{ data: { platforms: IConnectedPlatform[] } }>(connectedPlatformsUrl, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(res => setConnectedPlatforms(res.data.data.platforms ?? []))
            .catch(() => {});
    }, [token]);

    useEffect(() => {
        fetchConnected();
    }, [fetchConnected]);

    const hasAnyConnected = connectedPlatforms.length > 0;

    const handleCodeReceived = async (key: PlatformKey, code: string) => {
        const provider =
            key === 'instagram' ? resolveInstagramConnectProvider(buzzBridgeConfig) : PLATFORM_META[key].provider;
        setActionPlatform(key);
        setErrors(prev => ({ ...prev, [key]: undefined }));
        try {
            await dispatchPromise(postInfluencerOAuthConnect({ code, provider }));
            fetchConnected();
            onPlatformConnected?.();
        } catch (err: any) {
            const msg =
                err?.data?.message ||
                err?.response?.data?.message ||
                err?.message ||
                'Connection failed. Please try again.';
            setErrors(prev => ({ ...prev, [key]: msg }));
        } finally {
            setActionPlatform(null);
        }
    };

    const handleDisconnect = async (key: PlatformKey) => {
        setActionPlatform(key);
        setErrors(prev => ({ ...prev, [key]: undefined }));
        try {
            await axios.delete(`${connectedPlatformsUrl}/${key}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchConnected();
            onPlatformConnected?.();
        } catch {
            setErrors(prev => ({ ...prev, [key]: 'Disconnect failed. Please try again.' }));
        } finally {
            setActionPlatform(null);
        }
    };

    const getStats = (key: PlatformKey, platform: IPlatformStats | undefined) => [
        { label: key === 'youtube' ? 'Subscribers' : 'Followers', value: formatCompactNumber(platform?.followers) },
        { label: 'Engagement', value: formatPercent(platform?.engagementRate), highlight: true },
        { label: 'Avg Likes', value: formatCompactNumber(platform?.avg_likes) },
        { label: 'Avg Comments', value: formatCompactNumber(platform?.avg_comments) },
    ];

    return (
        <Box component="section">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: { xs: '1rem', md: '1.125rem' },
                        color: t.primary_white,
                    }}>
                    Connected Platforms
                </Typography>
                <Typography sx={{ fontSize: 11, color: t.placeholderGray, fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                    {hasAnyConnected ? 'Add another platform to sync stats' : 'Connect to sync your stats'}
                </Typography>
            </Box>

            {/* Horizontal on md+, stacked on mobile */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr',
                    gap: 1.5,
                }}>
                {(Object.keys(PLATFORM_META) as PlatformKey[]).map(key => {
                    const meta = PLATFORM_META[key];
                    const platform = formData[key];
                    const connInfo = connectedPlatforms.find(c => c.platform === key);
                    const isConnected = Boolean(connInfo);
                    const handle = connInfo?.platform_username
                        ? formatSocialHandle(connInfo.platform_username)
                        : platform?.username
                        ? formatSocialHandle(platform.username)
                        : null;
                    const stats = getStats(key, platform);
                    const isActing = actionPlatform === key;
                    const error = errors[key];
                    const isManualOpen = Boolean(manualOpen[key]);
                    const manualForm = getManualForm(key);
                    const existingHeadshotUrl = resolveInfluencerDisplayAvatarUrl(formData as IInfluencer);
                    const manualFormComplete = isManualPlatformLinkComplete(manualForm, { existingHeadshotUrl });

                    const autoLinkEnabled = isInfluencerPlatformAutoLinkEnabled(buzzBridgeConfig, key);

                    return (
                        <Box
                            key={key}
                            sx={{
                                borderRadius: '14px',
                                border: `1px solid ${isConnected ? 'rgba(255,255,255,0.08)' : borderColor}`,
                                bgcolor: cardBg,
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                            }}>
                            {/* Header */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 2 }}>
                                {/* Icon */}
                                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                    <Box
                                        sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '10px',
                                            background: meta.gradient,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#fff',
                                        }}>
                                        {meta.icon(20)}
                                    </Box>
                                    {isConnected && (
                                        <CheckCircleIcon
                                            sx={{
                                                position: 'absolute',
                                                bottom: -4,
                                                right: -4,
                                                fontSize: 15,
                                                color: '#00fdc1',
                                                bgcolor: cardBg,
                                                borderRadius: '50%',
                                            }}
                                        />
                                    )}
                                </Box>

                                {/* Name + handle */}
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontSize: 13,
                                            color: t.primary_white,
                                            lineHeight: 1.3,
                                        }}>
                                        {meta.label}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 11,
                                            color: isConnected ? '#00fdc1' : t.placeholderGray,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            mt: 0.25,
                                        }}
                                        noWrap>
                                        {handle ?? (isConnected ? 'Connected' : 'Not connected')}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Stats row — only when connected */}
                            {isConnected && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        borderTop: `1px solid ${borderColor}`,
                                    }}>
                                    {stats.map(stat => (
                                        <Box
                                            key={stat.label}
                                            sx={{
                                                px: 2,
                                                py: 1.25,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 2,
                                                borderBottom: `1px solid ${borderColor}`,
                                                '&:last-of-type': { borderBottom: 'none' },
                                            }}>
                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    color: t.placeholderGray,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.06em',
                                                    flexShrink: 0,
                                                }}>
                                                {stat.label}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    fontSize: 14,
                                                    color: stat.highlight ? t.highlight : t.primary_white,
                                                    lineHeight: 1.2,
                                                    textAlign: 'right',
                                                }}>
                                                {stat.value}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                            )}

                            {/* Not connected description */}
                            {!isConnected && (
                                <Box sx={{ px: 2, pb: 1.5, borderTop: `1px solid ${borderColor}`, pt: 1.25, flex: 1 }}>
                                    <Typography
                                        sx={{
                                            fontSize: 11,
                                            color: t.placeholderGray,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            lineHeight: 1.6,
                                        }}>
                                        Connect to sync followers, engagement &amp; stats.
                                    </Typography>
                                </Box>
                            )}

                            {/* Error */}
                            {error && (
                                <Box sx={{ px: 2, pb: 1 }}>
                                    <Typography
                                        sx={{ fontSize: 11, color: '#ff6b6b', fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                        {error}
                                    </Typography>
                                </Box>
                            )}

                            {/* Manual entry form */}
                            {isManualOpen && (
                                <Box
                                    sx={{
                                        px: 2,
                                        pt: 1.5,
                                        pb: 2,
                                        borderTop: `1px solid ${borderColor}`,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1.25,
                                    }}>
                                    <Typography
                                        sx={{
                                            fontSize: 10,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: t.placeholderGray,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.08em',
                                            mb: 0.5,
                                        }}>
                                        Enter stats manually
                                    </Typography>
                                    {LOCKED_PLATFORMS.includes(key) && (
                                        <Typography
                                            sx={{
                                                fontSize: 11,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                color: 'rgba(255,107,107,0.8)',
                                                lineHeight: 1.5,
                                                mb: 0.5,
                                                textAlign: 'justify',
                                            }}>
                                            Notice: Manually entered stats are unverified. Connect your account
                                            automatically to get a verified badge on your profile.
                                        </Typography>
                                    )}
                                    <ManualPlatformLinkForm
                                        value={manualForm}
                                        onChange={data => handleManualFieldChange(key, data)}
                                        showAutoLinkUnavailableNotice
                                        existingHeadshotUrl={existingHeadshotUrl}
                                        {...manualLinkLabels(key)}
                                        mutedColor={t.placeholderGray}
                                        highlightColor={t.highlight}
                                        textFieldSx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                                fontSize: 12,
                                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                color: t.primary_white,
                                                bgcolor: themeStyle === 'dark' ? '#1a1a1a' : t.secondaryBg,
                                                minHeight: 44,
                                                '& fieldset': { borderColor },
                                                '&:hover fieldset': { borderColor: t.highlight },
                                                '&.Mui-focused fieldset': { borderColor: t.highlight },
                                            },
                                        }}
                                        fieldLabelSx={{
                                            fontSize: 10,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            color: t.placeholderGray,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.08em',
                                            mb: 0.5,
                                        }}
                                    />
                                    <Stack direction="row" gap={1} mt={0.5}>
                                        <Button
                                            size="small"
                                            onClick={() => handleManualToggle(key)}
                                            sx={{
                                                flex: 1,
                                                fontSize: 11,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                textTransform: 'none',
                                                borderRadius: 999,
                                                py: 0.75,
                                                color: t.placeholderGray,
                                                border: `1px solid ${borderColor}`,
                                                '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                                            }}>
                                            Cancel
                                        </Button>
                                        <Button
                                            size="small"
                                            startIcon={<CheckIcon sx={{ fontSize: '14px !important' }} />}
                                            disabled={!manualFormComplete}
                                            onClick={() => handleManualSubmit(key)}
                                            sx={{
                                                flex: 2,
                                                fontSize: 11,
                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                textTransform: 'none',
                                                borderRadius: 999,
                                                py: 0.75,
                                                color: '#131313',
                                                bgcolor: t.highlight,
                                                '&:hover': { bgcolor: t.highlight, opacity: 0.9 },
                                            }}>
                                            Save stats
                                        </Button>
                                    </Stack>
                                </Box>
                            )}

                            {/* Action buttons */}
                            <Box
                                sx={{
                                    px: 2,
                                    pb: 2,
                                    pt: isConnected || isManualOpen ? 0 : 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 0.75,
                                }}>
                                {isActing ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
                                        <CircularProgress size={18} sx={{ color: t.highlight }} />
                                    </Box>
                                ) : isConnected ? (
                                    <Button
                                        fullWidth
                                        size="small"
                                        startIcon={<LinkOffIcon sx={{ fontSize: '14px !important' }} />}
                                        onClick={() => handleDisconnect(key)}
                                        sx={{
                                            fontSize: 11,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            textTransform: 'none',
                                            borderRadius: 999,
                                            py: 0.75,
                                            color: '#ff6b6b',
                                            border: '1px solid rgba(255,107,107,0.3)',
                                            bgcolor: 'rgba(255,107,107,0.06)',
                                            '&:hover': {
                                                bgcolor: 'rgba(255,107,107,0.12)',
                                                borderColor: 'rgba(255,107,107,0.5)',
                                            },
                                        }}>
                                        Disconnect
                                    </Button>
                                ) : !isManualOpen ? (
                                    <>
                                        {LOCKED_PLATFORMS.includes(key) ? (
                                            <Button
                                                fullWidth
                                                size="small"
                                                disabled
                                                sx={{
                                                    fontSize: 11,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    textTransform: 'none',
                                                    borderRadius: 999,
                                                    py: 0.75,
                                                    '&.Mui-disabled': {
                                                        cursor: 'not-allowed',
                                                        pointerEvents: 'all',
                                                        color: t.placeholderGray,
                                                        border: `1px solid ${t.highlight}55`,
                                                        bgcolor: 'transparent',
                                                    },
                                                }}>
                                                Connect
                                            </Button>
                                        ) : autoLinkEnabled ? (
                                            <SocialLogin
                                                provider={
                                                    key === 'instagram'
                                                        ? resolveInstagramConnectProvider(buzzBridgeConfig)
                                                        : meta.provider
                                                }
                                                onCodeReceived={code => handleCodeReceived(key, code)}
                                                onAuthComplete={ok => {
                                                    if (!ok) setActionPlatform(null);
                                                }}
                                                customButton={
                                                    <Button
                                                        fullWidth
                                                        size="small"
                                                        sx={{
                                                            fontSize: 11,
                                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                                            textTransform: 'none',
                                                            borderRadius: 999,
                                                            py: 0.75,
                                                            color: t.highlight,
                                                            border: `1px solid ${t.highlight}55`,
                                                            bgcolor: `${t.highlight}14`,
                                                            '&:hover': {
                                                                bgcolor: `${t.highlight}22`,
                                                                borderColor: t.highlight,
                                                            },
                                                        }}>
                                                        {hasAnyConnected ? 'Connect another' : 'Connect'}
                                                    </Button>
                                                }
                                            />
                                        ) : null}
                                        {onManualSave && (
                                            <Button
                                                fullWidth
                                                size="small"
                                                startIcon={<EditOutlinedIcon sx={{ fontSize: '14px !important' }} />}
                                                onClick={() => handleManualToggle(key)}
                                                sx={{
                                                    fontSize: 11,
                                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                                    textTransform: 'none',
                                                    borderRadius: 999,
                                                    py: 0.75,
                                                    color: t.placeholderGray,
                                                    border: `1px solid ${borderColor}`,
                                                    '&:hover': {
                                                        bgcolor: 'rgba(255,255,255,0.04)',
                                                        color: t.primary_white,
                                                    },
                                                }}>
                                                Enter manually
                                            </Button>
                                        )}
                                    </>
                                ) : null}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};
